<script type="text/javascript">
	import mapboxgl from 'mapbox-gl';
	import { onMount, onDestroy } from "svelte";

	import Textfield, {Input, Textarea} from "@smui/textfield";
	import HelperText from "@smui/textfield/helper-text/index";
	import MenuItem from "../FlyoutMenu/MenuItem.svelte";
	import LayerListSwitch from '../LayerList/LayerListSwitch.svelte';

	import MapLayerManager from "../../Map/MapLayerManager";
	import MapLayer from "../../Map/Enums/MapLayer";
	import MapDataSource from "../../Map/Enums/MapDataSource";
	import MapStore from "../../Map/Store";

	import GeoHelper from "../../Helpers/GeoHelper";

	import InvestigateDataGapsStore from "./InvestigateDataGapsStore";

	import Vessel from "../../Models/Vessel";

	import MySvelteTable from "../AlertList/MySvelteTable.svelte";
	import VesselHistoryStore from './Store';

	export let dataPoints = [];
	export let dataSource = MapDataSource.HIGHLIGHT_LINES;
	export let layer = MapLayer.HIGHLIGHT_LINES;
	export let layerEnabled = InvestigateDataGapsStore.enabled;
	export let gapLength = InvestigateDataGapsStore.gapLength;

	let map = MapStore.mapbox.instance;
	let mapInitialised = MapStore.meta.initialised;
	let mapLayersInitted = VesselHistoryStore.meta.mapInitted;

	let gapsLines = [];
	let tableRowData = [];
	let selectedLine = null;

	let fromPointPopup = new mapboxgl.Popup({
		closeButton: false,
		closeOnClick: true
	});

	let toPointPopup = new mapboxgl.Popup({
		closeButton: false,
		closeOnClick: true
	});
	toPointPopup.on("close", function(e) {
        selectedLine = null;
    });

	let formatTime = (t) => {
		return Vessel.formatTime(new Date(t).toISOString().substr(0, 19))
	};
	let calcHours = (from ,to) => {
		return Math.floor((to - from) / 1000.0 / 60.0 / 60.0); // number of hours
	};

	let onCellClick = (event) => {
		const columnKey = event.detail.key;
		const rowData = event.detail.row;

		const imo = rowData.imo;
		const fromTimestamp = rowData.fromTimestamp;
		const toTimestamp = rowData.toTimestamp;

		// Find LineString by imo/from/to
		let result = null;
		let prevSelectedLine = selectedLine;
		selectedLine = null;
		for(let i = 0; i < gapsLines.length; i++) {
			const line = gapsLines[i];
			if (line.properties.imo == imo && line.properties.fromTimestamp == fromTimestamp && line.properties.toTimestamp == toTimestamp) {
				result = line;
				break;
			}
		}
		// If it exists, focus on it!
		if (result) {
			const from = result.geometry.coordinates[0];
			const to = result.geometry.coordinates[1];
			MapStore.helpers.zoomToLine(from, to);

			const fromSpeed = result.properties.fromSpeed;
			const toSpeed = result.properties.toSpeed;
			const fromTime = result.properties.fromTimestamp;
			const toTime = result.properties.toTimestamp;
			const hrs = calcHours(fromTime, toTime);

			let nl = "<br/>";
			let fromHtml = "";
			fromHtml += "<b>IMO:</b> " + result.properties.imo + nl;
			//fromHtml += "<b>Speed</b>: " + fromSpeed + " knots" + nl;
			fromHtml += "<b>Offline At:</b><br/>" + formatTime(fromTime);
			fromPointPopup.setHTML(fromHtml);
			fromPointPopup.setLngLat(from);
			fromPointPopup.addTo($map);

			let toHtml = "";
			toHtml += "<b>Hours Offline:</b> " + hrs + nl;
			//toHtml += "<b>Speed</b>: " + toSpeed + " knots" + nl;
			toHtml += "<b>Back Online At:</b><br/>" + formatTime(toTime);
			toPointPopup.setHTML(toHtml);
			toPointPopup.setLngLat(to);
			toPointPopup.addTo($map);

			selectedLine = result;
		}
		if (selectedLine !== null &&
			prevSelectedLine !== null &&
			selectedLine.properties.imo === prevSelectedLine.properties.imo &&
			selectedLine.properties.fromTimestamp === prevSelectedLine.properties.fromTimestamp
			) {
			selectedLine = null;
			toPointPopup.remove();
			fromPointPopup.remove();
		}

	};

	let tableColumns = [
		{
			key: "imo",
			title: "IMO",
			value: v => v.imo,
			sortable: true,
			renderValue: (v, v2) => {
				return v.imo;
			},
			class: v => {
				return v.selected ? "selected" : "";
			},
			headerClass: "imo"
		},
		{
			key: "fromTimestamp",
			title: "From",
			value: v => v.fromTimestamp,
			renderValue: v => "<span title='" + v.fromTimestamp + "'>" + formatTime(v.fromTimestamp) + "</span>",
			sortable: true,
			class: v => {
				return " " + (v.selected ? "selected" : "");
			},
		},
		{
			key: "length",
			title: "Length",
			value: v => v.lengthHours,
			renderValue: v => "<span title='" + formatTime(v.toTimestamp) + "'>" + v.lengthHours + " hrs</span>",
			sortable: true,
			class: v => {
				return " " + (v.selected ? "selected" : "");
			},
			headerClass: "imo"
		}
	];

	onMount(() => {
		gapLength.subscribe(() =>{
			selectedLine = null;
			fromPointPopup.remove();
			toPointPopup.remove();
			update(dataPoints, $gapLength);
		});
		layerEnabled.subscribe(() => {
			selectedLine = null;
			fromPointPopup.remove();
			toPointPopup.remove();
			update(dataPoints, $gapLength);
		});
	});

	onDestroy(()=>{
		toPointPopup.remove();
		fromPointPopup.remove();
	});

	$: {
		layerEnabled;
		$layerEnabled;
		$gapLength;
		selectedLine;
		update(dataPoints, $gapLength);
	}

	let layerChange = (event) => {
		const layer = event.target.dataset.layer;
		const storeKey = event.target.dataset['storekey'];
		const checked = event.target.checked;
		$layerEnabled = checked;
		MapLayerManager.updateVisibleLayers();
	};

	// Add highlighted lines data. (24 hours)
	let update = (points, timeHours = 24) => {

		if ($map && $mapInitialised && $mapLayersInitted) {
			gapsLines = GeoHelper.generateTimeGapLines(points, timeHours*3600*1000);
			tableRowData = [];
			if ((""+timeHours).length > 0 && parseFloat(timeHours) > 0 && $layerEnabled) {
				for (let i = 0; i < gapsLines.length; i++) {
					let row = {
						imo: gapsLines[i].properties.imo,
						fromTimestamp: gapsLines[i].properties.fromTimestamp,
						toTimestamp: gapsLines[i].properties.toTimestamp,
						lengthHours: calcHours(gapsLines[i].properties.fromTimestamp, gapsLines[i].properties.toTimestamp),
						selected: selectedLine !== null && gapsLines[i].properties.fromTimestamp === selectedLine.properties.fromTimestamp
					};
					tableRowData.push(row);
				}
			}

			const gapLinesFiltered = gapsLines.filter((lineFeature) => {
				if (selectedLine == null) {
					return true;
				}
				else {
					// console.log(lineFeature.properties.fromTimestamp, selectedLine.properties.fromTimestamp);
					if (lineFeature.properties.fromTimestamp === selectedLine.properties.fromTimestamp) {
						return true;
					}
					return false;
				}
			});
			// console.log("gapLinesFiltered", gapLinesFiltered);
			// MapLayer.HIGHLIGHT_LINES 
			$map.getSource(dataSource).setData(GeoHelper.emptyGeoJson(gapLinesFiltered));
		}
	};
	export const updateData = () => {
		update(dataPoints, $gapLength);
	}
	export const reset = () => {
		dataPoints = [];
		updateData();
	}



</script>

<style type="text/css">
	.investigate-list-component span {
	  font-size: 10px;
	}
	table tr td, span {
		font-size: 10px;
	}
	:global(.aisInputGapTF) {
	    width: 20px !important;
	    background-color: white !important;
	    height: 100% !important;
	    padding: 1px!important;
	    display: inline-block !important;
	    vertical-align: baseline !important;
	    font-size: 12px !important;
	    text-align: center !important;
	}

</style>

<MenuItem title={"Investigate Data Gaps"}>

	<!-- <div class="menu-subelement layer-component">
		<span>AIS data gaps</span>
		<div>
			<label class="switch">
				<input
					type="checkbox"
					data-layer={layer}
					data-storekey="dataGaps"
					checked="{$layerEnabled}"
					on:change={layerChange}
					/>
				<span class="slider" />
			</label>
		</div>
	</div> -->
	<div class='layer-list-component'>
		<LayerListSwitch
			label={"AIS data gaps"}
			data-storekey="dataGaps"
			data-layer={layer}
			checked="{$layerEnabled}"
			on:change={layerChange}
		>
			<span slot="label">
				(<input type="text" class="aisInputGapTF" rows="1" wrap="soft" bind:value={$gapLength} placeholder="number of "/> hours)
			</span>
		</LayerListSwitch>

	</div>
	{#if $layerEnabled}
		<!-- <Textfield fullwidth variant="filled" bind:value={$gapLength} label="Gap length:"
			input$aria-controls="datagaps-helper"
			input$aria-describedby="datagaps-helper"/>
		<HelperText id="datagaps-helper" persistent>Hours</HelperText> -->

		<div class="selection-list-element" style="clear:both;">
			<div class="alert-list-content mdc-elevation--z2 investigate-list-component" style="height:auto;">
				<MySvelteTable on:clickCell={onCellClick} rows={tableRowData} columns={tableColumns} />
			</div>
		</div>
	{/if}
</MenuItem>
